import { calculateContrast, getWCAGLevel } from '@/utils/leonardo/contrast';
import { getAPCALevel } from '@/utils/leonardo/apca';
import { getColorsByName } from '@/utils/leonardo/css';
import { createTheme } from '@/utils/leonardo/theme';
import { normalizeHex } from './normalizeHex';

export interface GenerateColorScaleInput {
  baseColor: string;
  mode?: 'light' | 'dark';
  formula?: 'wcag2' | 'wcag3';
}

export interface ScaleSwatch {
  step: number;
  hex: string;
  contrastRatio: number;
  wcagLevel: ReturnType<typeof getWCAGLevel> | ReturnType<typeof getAPCALevel>;
}

export interface GenerateColorScaleResult {
  baseColor: string;
  mode: 'light' | 'dark';
  formula: 'wcag2' | 'wcag3';
  background: string;
  swatches: ScaleSwatch[];
}

export const executeGenerateColorScale = (
  input: GenerateColorScaleInput
): GenerateColorScaleResult => {
  const baseColor = normalizeHex(input.baseColor);
  const mode = input.mode ?? 'light';
  const formula = input.formula ?? 'wcag2';

  const theme = createTheme(
    {
      baseColors: { blue: baseColor },
      backgroundColor: mode === 'dark' ? 'Black' : 'White',
      formula,
    },
    mode
  );

  const background = theme.contrastColorPairs.background as string;
  const blueScale = getColorsByName(theme).blue ?? [];

  const swatches: ScaleSwatch[] = blueScale
    .sort((a, b) => a.step - b.step)
    .map(({ step, value }) => {
      const contrastRatio = calculateContrast(value, background, formula);
      const roundedRatio = Math.round(contrastRatio * 100) / 100;

      return {
        step,
        hex: value.toUpperCase(),
        contrastRatio: roundedRatio,
        wcagLevel:
          formula === 'wcag3'
            ? getAPCALevel(contrastRatio)
            : getWCAGLevel(contrastRatio),
      };
    });

  return {
    baseColor,
    mode,
    formula,
    background: background.toUpperCase(),
    swatches,
  };
};
